import { FormProvider } from "react-hook-form";
import { useLocation } from "react-router-dom-v5-compat";

import { Button, Stack } from "@seneca/senekit";

import ControlledTextInput from "seneca-common/utils/form-hook/inputs/ControlledTextInput";
import {
  validateControlledEmailLoosely,
  validateControlledPassword
} from "seneca-common/utils/form-hook/utils/validationFunctions";

import {
  ErrorMessage,
  LogInLink,
  TermsAndConditionsCheckbox
} from "features/sign-up/common-routes/components/form";
import { useSignUpEmailForm } from "features/sign-up/common-routes/components/hooks";
import { SignUpCardContent } from "features/sign-up/common-routes/components/Layout";
import useOnSignUpSuccess from "features/sign-up/hooks/useOnSignUpSuccess";

import { SignUpFormHeader } from "../SignUpHeaders";

type Props = {
  prefilledEmail?: string;
  accountType?: string;
  linkToNextPage?: string;
  linkToSignIn: string;
  headerMessage?: string;
};

export default function SignUpWithEmailAndPasswordForm({
  accountType,
  linkToNextPage,
  prefilledEmail,
  linkToSignIn,
  headerMessage
}: Props) {
  const location = useLocation();

  const onSignUpSuccess = useOnSignUpSuccess({
    accountType: accountType,
    linkToNextPage: linkToNextPage,
    search: location.search
  });

  const { form, handleSubmit, errorMessage } = useSignUpEmailForm({
    email: prefilledEmail,
    accountType,
    onSubmitSuccess: onSignUpSuccess
  });

  return (
    <FormProvider {...form}>
      <SignUpCardContent>
        {{
          body: (
            <Stack space="xlarge">
              <SignUpFormHeader headerMessage={headerMessage} />
              {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
              <ControlledTextInput
                name="email"
                label="Email"
                type="email"
                autoFocus
                validateObj={validateControlledEmailLoosely}
              />
              <ControlledTextInput
                name="password"
                label="Password"
                type="password"
                autoFocus={!!prefilledEmail}
                validateObj={validateControlledPassword}
              />
              <TermsAndConditionsCheckbox />
            </Stack>
          ),
          footer: (
            <Stack space="xxlarge">
              <Button
                type="submit"
                width="100%"
                onClick={handleSubmit}
                loading={form.formState.isSubmitting}
              >
                Sign up with email
              </Button>
              <LogInLink loginLink={linkToSignIn} />
            </Stack>
          )
        }}
      </SignUpCardContent>
    </FormProvider>
  );
}
